import { useEffect, useState } from 'react';
import { earlyAccessApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { StatusPill } from '../../components/StatusPills';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * The coming-soon waiting list: who has asked to be told when the site
 * opens, whether they have been emailed, and whether they have actually
 * registered. "Mark invited" is a plain tracking aid for sending in
 * batches — the invite link itself is what grants access, not this flag.
 */
const AdminEarlyAccess = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, invited: 0, converted: 0 });
  const [comingSoon, setComingSoon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await earlyAccessApi.adminList();
      setRows(data.signups);
      setCounts({ total: data.total, invited: data.invited, converted: data.converted });
      setComingSoon(data.coming_soon);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the waiting list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleExport = async () => {
    try {
      const res = await earlyAccessApi.adminExportCsv();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `early-access-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not export the list');
    }
  };

  const handleMarkInvited = async (row) => {
    setMarking(row.id);
    try {
      const { data } = await earlyAccessApi.adminMarkInvited(row.id);
      setRows((prev) => prev.map((r) => (r.id === row.id ? data : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update this signup');
    } finally {
      setMarking(null);
    }
  };

  const handleCopyLink = async (row) => {
    try {
      await navigator.clipboard.writeText(row.invite_link);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId((id) => (id === row.id ? null : id)), 1500);
    } catch {
      toast.error('Could not copy the link');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.03em]">Early access</h1>
          <p className="text-ink-70 mt-1">
            {comingSoon === false
              ? 'The site is open — registrations no longer need an invite link.'
              : 'The site shows the waiting list. Send someone their invite link below and they can register while everyone else still sees "coming soon".'}
          </p>
        </div>
        <Button variant="secondary" onClick={handleExport} data-testid="early-access-export-btn">
          <Download className="w-4 h-4" aria-hidden="true" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-md">
        <div className="rounded-lg bg-ground p-4">
          <p className="font-heading text-[28px] font-bold">{counts.total}</p>
          <p className="text-[13px] text-ink-55">On the list</p>
        </div>
        <div className="rounded-lg bg-ground p-4">
          <p className="font-heading text-[28px] font-bold">{counts.invited}</p>
          <p className="text-[13px] text-ink-55">Invited</p>
        </div>
        <div className="rounded-lg bg-brand-wash p-4">
          <p className="font-heading text-[28px] font-bold text-brand-ink">{counts.converted}</p>
          <p className="text-[13px] text-brand-ink">Registered</p>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-ink-55" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-ground p-8 text-center">
          <p className="font-heading text-[20px] font-semibold">Nothing here yet</p>
          <p className="text-ink-55 mt-1">Signups appear here as people join the waiting list.</p>
        </div>
      ) : (
        <div className="rounded-lg bg-ground overflow-x-auto">
          <Table data-testid="early-access-table">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-testid={`early-access-row-${row.id}`}>
                  <TableCell className="font-medium">{row.email}</TableCell>
                  <TableCell className="text-ink-70">{row.full_name || '—'}</TableCell>
                  <TableCell className="pd-mono normal-case text-ink-55">
                    {row.created_at ? new Date(row.created_at).toLocaleDateString('en-GB') : '—'}
                  </TableCell>
                  <TableCell>
                    {row.converted ? (
                      <StatusPill tone="done">Registered</StatusPill>
                    ) : row.invited ? (
                      <StatusPill tone="held">Invited</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Waiting</StatusPill>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-2 whitespace-nowrap">
                    {!row.converted && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyLink(row)}
                          data-testid={`early-access-copy-${row.id}`}
                        >
                          {copiedId === row.id ? 'Copied' : 'Copy invite link'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={row.invited || marking === row.id}
                          onClick={() => handleMarkInvited(row)}
                          data-testid={`early-access-mark-invited-${row.id}`}
                        >
                          {marking === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (row.invited ? 'Invited' : 'Mark invited')}
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AdminEarlyAccess;
